import type {
  V1alpha1Feature,
  V1alpha1FeatureGate
} from '@streamnative/cloud-api-client-typescript'
import { useCloudApi, useComputeApi } from '@/api/cloudApi'
import { getErrorMessage } from '@/utils/apiHelper'
import type { PulsarState } from './usePulsarState'

let lastOrg: string | undefined = undefined

const features = ref<V1alpha1Feature[]>([])
const featureGates = ref<V1alpha1FeatureGate[]>([])

// Tracks which features have dependent resources that prevent disabling
const featureResourceDeps = ref<Record<string, string>>({})

const fetchFeatures = async (organization: string) => {
  try {
    const api = useCloudApi()
    const [featuresRes, featureGatesRes] = await Promise.all([
      api.listFeature(),
      api.listNamespacedFeatureGate(organization)
    ])
    features.value = featuresRes.data.items ?? []
    featureGates.value = featureGatesRes.data.items ?? []
    await checkFeatureResourceDeps(organization)
  } catch {
    features.value = []
    featureGates.value = []
    featureResourceDeps.value = {}
  }
}

const checkFeatureResourceDeps = async (organization: string) => {
  const deps: Record<string, string> = {}
  try {
    const [kafkaRes, workspaceRes] = await Promise.all([
      useCloudApi()
        .listNamespacedKafkaCluster(
          organization,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          1
        )
        .catch(() => null),
      useComputeApi()
        .listNamespacedWorkspace(
          organization,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          1
        )
        .catch(() => null)
    ])
    if (kafkaRes?.data?.items && kafkaRes.data.items.length > 0) {
      deps['native-kafka'] =
        'KafkaCluster resources exist in this organization. Please delete all Kafka clusters before disabling this feature.'
    }
    if (workspaceRes?.data?.items && workspaceRes.data.items.length > 0) {
      deps['compute-workspace'] =
        'Workspace resources exist in this organization. Please delete all workspaces before disabling this feature.'
    }
  } catch {
    // Ignore errors - if we can't check, allow the toggle
  }
  featureResourceDeps.value = deps
}

const getDefaultEnabled = (phase: string): boolean => {
  return phase === 'GA_OptOut' || phase === 'GA_AlwaysOn'
}

const isFeatureEnabled = (featureName: string): boolean => {
  const fg = featureGates.value.find(fg => fg.spec?.feature === featureName)
  if (fg?.spec?.enabled !== undefined) {
    return fg.spec.enabled
  }
  const feature = features.value.find(f => f.metadata?.name === featureName)
  if (feature?.spec?.phase) {
    return getDefaultEnabled(feature.spec.phase)
  }
  return false
}

const getPhaseBadge = (phase: string): string => {
  switch (phase) {
    case 'PrivatePreview':
      return 'Private Preview'
    case 'PublicPreview':
      return 'Public Preview'
    case 'GA_OptIn':
      return 'GA (Opt-In)'
    case 'GA_OptOut':
      return 'GA (Opt-Out)'
    case 'GA_AlwaysOn':
      return 'Generally Available'
    default:
      return phase
  }
}

// --- Feature Stage Badge System ---
// Centralized registry for feature stage badges that handles both
// API-based feature phases and static context-dependent overrides.

export type ClusterResourceType = 'kafka' | 'pulsar' | undefined

export type ClusterFeatureStageContext = {
  resourceType: ClusterResourceType
  isServerless?: boolean
}

// Static badge overrides for features not tracked in the preview feature API,
// or features whose badge depends on the current resource type.
const staticFeatureStages: Record<string, (ctx: ClusterFeatureStageContext) => string | undefined> =
  {
    connectors: ctx => (ctx.resourceType === 'kafka' ? 'Coming Soon' : undefined),
    'native-kafka': ctx => (ctx.isServerless ? 'Private Preview' : undefined)
  }

// Returns the badge label for a feature, checking:
// 1. Static overrides first (for context-dependent badges like "Coming Soon")
// 2. API-based feature phase (for preview features like "Public Preview")
// Returns undefined if the feature is GA or not found (no badge needed).
const getFeatureStageBadge = (
  featureId: string,
  ctx?: ClusterFeatureStageContext
): string | undefined => {
  const staticFn = staticFeatureStages[featureId]
  if (staticFn && ctx) {
    const result = staticFn(ctx)
    if (result) return result
  }

  const feature = features.value.find(f => f.metadata?.name === featureId)
  if (feature?.spec?.phase) {
    const phase = feature.spec.phase
    if (phase === 'PrivatePreview' || phase === 'PublicPreview') {
      return getPhaseBadge(phase)
    }
  }
  return undefined
}

const isPreviewFeature = (feature: V1alpha1Feature): boolean => {
  const phase = feature.spec?.phase
  return phase === 'PrivatePreview' || phase === 'PublicPreview' || phase === 'GA_OptIn'
}

const previewFeatures = computed(() => {
  return features.value.filter(isPreviewFeature).map(feature => {
    const featureName = feature.metadata?.name ?? ''
    const phase = feature.spec?.phase ?? ''
    return {
      id: featureName,
      name: feature.spec?.displayName || featureName,
      description: feature.spec?.description || '',
      enabled: isFeatureEnabled(featureName),
      badge: getPhaseBadge(phase),
      documentationUrl: feature.spec?.documentationURL,
      feedbackUrl: undefined as string | undefined,
      phase
    }
  })
})

const updateFeatureGate = async (org: string, featureId: string, enabled: boolean) => {
  try {
    const api = useCloudApi()
    const existingGate = featureGates.value.find(fg => fg.spec?.feature === featureId)

    if (existingGate && existingGate.metadata?.name) {
      await api.patchNamespacedFeatureGate(existingGate.metadata.name, org, [
        {
          op: 'replace',
          path: '/spec/enabled',
          value: enabled
        }
      ])
    } else {
      const newFeatureGate: V1alpha1FeatureGate = {
        apiVersion: 'cloud.streamnative.io/v1alpha1',
        kind: 'FeatureGate',
        metadata: {
          name: featureId,
          namespace: org
        },
        spec: {
          feature: featureId,
          enabled
        }
      }
      await api.createNamespacedFeatureGate(org, newFeatureGate)
    }
  } catch (e) {
    throw Error(getErrorMessage(e, 'updateFeatureGate Error'))
  }
}

export const useFeature = () => {
  return {
    features,
    featureGates,
    featureResourceDeps,
    previewFeatures,
    fetchFeatures,
    isFeatureEnabled,
    getFeatureStageBadge,
    getPhaseBadge,
    updateFeatureGate,
    init
  }
}

const init = (initialState: PulsarState) => {
  const { organization } = usePulsarState()
  const { isRbacUpdating } = rbacHelper()

  const valueChanged = async ([org, ab]: [string | undefined, boolean | undefined]) => {
    if (!org) {
      features.value = []
      featureGates.value = []
      lastOrg = undefined
      return
    }
    if (ab) {
      return
    }

    if (org !== lastOrg) {
      await fetchFeatures(org)
    }
    lastOrg = org
  }

  watch([organization, isRbacUpdating], valueChanged)
  return valueChanged([initialState.organization, isRbacUpdating.value])
}
